import * as React from 'react'
import { Copy, Check } from '@phosphor-icons/react'
import { cn } from '../../lib/utils'

/**
 * DetailHeader — the top of a detail page's main column: an optional eyebrow +
 * copyable record ID, a title, a row of status / freshness stats, and a
 * right-aligned actions cluster (Inbox, an Actions menu). Used across entity
 * types (deal, company, …) — pass whichever slots the record has.
 *
 *   <DetailHeader
 *     eyebrow="Deal" identifier="10482" title="Q3 Reorder"
 *     stats={<><StatusStat …/><StatusStat …/></>}
 *     actions={<><Button>Inbox</Button><Button>Actions</Button></>}
 *   />
 *
 * `stats` is a slot — compose `StatusStat`s (freshness) or `Stat`s; the header
 * lays them out in a wrapping row. `identifier` renders as `#{id}` with a
 * click-to-copy button. `title` is optional (omit for a compact, actions-only
 * header).
 */

interface CopyableIdProps {
  // string | number, not ReactNode: the value is stringified for the clipboard
  // write and the aria-label, so JSX/objects would degrade to "[object Object]".
  value: string | number
}

/** The `#{id}` chip — click to copy, with a copy → check confirmation. */
const CopyableId = ({ value }: CopyableIdProps) => {
  const [copied, setCopied] = React.useState(false)
  const onCopy = () => {
    // Only confirm on a real, resolved copy. `clipboard` is undefined in
    // non-secure/older contexts (optional chaining → no promise), and writeText
    // can reject (permission denied) — in both cases we show no confirmation.
    const write = navigator.clipboard?.writeText(`${value}`)
    if (!write) return
    void write.then(
      () => {
        setCopied(true)
        // Reset shortly after (event-handler timer, not an effect).
        setTimeout(() => setCopied(false), 1500)
      },
      () => {
        /* copy failed/denied — leave the icon in its default state */
      }
    )
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      aria-label={copied ? 'Copied' : `Copy ID ${value}`}
      className={cn(
        'inline-flex items-center gap-1 rounded text-sm tabular-nums text-fbui-gray-500 outline-none transition-colors',
        'hover:text-fbui-gray-700 focus-visible:ring-2 focus-visible:ring-fbui-ring-brand [&>svg]:size-3.5'
      )}
    >
      #{value}
      {copied ? (
        <Check weight="bold" className="text-fbui-basil-600" aria-hidden="true" />
      ) : (
        <Copy aria-hidden="true" />
      )}
    </button>
  )
}

export interface DetailHeaderProps {
  /** Copyable record identifier — rendered as `#{id}` with a copy button. */
  identifier?: string | number
  /** Quiet uppercase eyebrow above the title (the entity type). */
  eyebrow?: React.ReactNode
  /** Entity title. Omit for a title-less header (identity elsewhere). */
  title?: React.ReactNode
  /** Muted secondary line under the title — a role · company, a segment. */
  subtitle?: React.ReactNode
  /** Status / freshness stats row — compose `StatusStat`s or `Stat`s. */
  stats?: React.ReactNode
  /** Right-aligned actions — Inbox button, Actions menu. */
  actions?: React.ReactNode
  /** Heading level for the title. Default 1. */
  headingLevel?: 1 | 2 | 3
  className?: string
}

export const DetailHeader = ({
  identifier,
  eyebrow,
  title,
  subtitle,
  stats,
  actions,
  headingLevel = 1,
  className,
}: DetailHeaderProps) => {
  const Heading = `h${headingLevel}` as 'h1'
  const hasTopLine = eyebrow != null || identifier != null
  return (
    <header className={cn('flex items-start justify-between gap-4', className)}>
      <div className="flex min-w-0 flex-col gap-1">
        {hasTopLine && (
          <div className="flex items-center gap-2">
            {eyebrow != null && (
              <span className="text-xs font-semibold uppercase tracking-wide text-fbui-gray-500">
                {eyebrow}
              </span>
            )}
            {identifier != null && <CopyableId value={identifier} />}
          </div>
        )}
        {title != null && (
          <Heading className="m-0 truncate text-2xl font-semibold text-fbui-gray-900">
            {title}
          </Heading>
        )}
        {subtitle != null && (
          <p className="truncate text-sm text-fbui-gray-500">{subtitle}</p>
        )}
        {stats != null && (
          <div className="mt-1 flex flex-wrap items-center gap-x-6 gap-y-2">
            {stats}
          </div>
        )}
      </div>
      {actions != null && (
        <div className="flex shrink-0 items-center gap-2">{actions}</div>
      )}
    </header>
  )
}
DetailHeader.displayName = 'DetailHeader'
